import { format, formatDistanceStrict, parseISO } from "date-fns";
import { useContext } from "react";
import { QueryClient, useQuery } from "react-query";

import { AppContext } from "../components/AppContextProvider";
import { getDefaultChart } from "./getDefaultChart";
import { supabase } from "./supabaseClient";

export const queryClient = new QueryClient();

queryClient.setDefaultOptions({
  queries: {
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  },
});

/**
 * Send an email
 */
export async function mail(email: {
  from: string;
  text: string;
  to: string;
  subject: string;
}): Promise<{ success: boolean }> {
  const response = await fetch("/api/mail", {
    method: "POST",
    mode: "cors",
    cache: "no-cache",
    credentials: "same-origin",
    body: JSON.stringify({ email }),
    headers: { "Content-Type": "application/json" },
  });
  const result = await response.json();
  if (!result.success) throw new Error("Failed to send");
  return result;
}

async function orderHistory(
  customerId?: string,
  subscriptionId?: string
): Promise<
  {
    id: string;
    amount_paid: number;
    created: number;
  }[]
> {
  if (!customerId || !subscriptionId) return [];
  const response = await fetch("/api/order-history", {
    method: "post",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      customerId,
      subscriptionId,
    }),
  });
  const { error, invoices } = await response.json();
  if (error) throw error;
  return invoices;
}

export function useOrderHistory(customerId?: string, subscriptionId?: string) {
  return useQuery(
    ["auth", "orderHistory", customerId, subscriptionId],
    () => orderHistory(customerId, subscriptionId),
    {
      enabled: Boolean(customerId) && Boolean(subscriptionId),
      suspense: true,
    }
  );
}

type MakeChartArgs = {
  name: string;
  user_id: string;
  chart?: string;
} & (
  | { fromPrompt: true; prompt: string; method: "instruct" | "extract" }
  | { fromPrompt?: never; prompt?: never; method?: never }
  | { template: string; fromPrompt?: never; prompt?: never; method?: never }
);

export async function makeChart({
  name,
  user_id,
  chart,
  ...rest
}: MakeChartArgs) {
  if (!supabase) return;
  const defaultText = getDefaultChart();

  if ("fromPrompt" in rest && rest.fromPrompt) {
    // get supabase session
    const { data } = await supabase.auth.getSession();
    if (!data.session) throw new Error("No session");

    const accessToken = data.session.access_token;

    const response = await fetch(`/api/prompt/${rest.method}`, {
      method: "post",
      body: JSON.stringify({ prompt: rest.prompt }),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    const result = await response.json();

    if ("chart" in result) chart = result["chart"] as string;
  } else if ("template" in rest) {
    // get template here
    try {
      const template = await import(`./templates/${rest.template}-template.ts`);
      chart = template.default;
    } catch (error) {
      console.error(error);
    }
  }

  return supabase
    .from("user_charts")
    .insert({
      name,
      chart: chart ?? defaultText,
      user_id,
      created_at: new Date(),
      updated_at: new Date(),
    })
    .select();
}

export async function copyHostedChartById(id: number) {
  if (!supabase) return;
  // get current user
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session) throw new Error("No session");
  const { data, error } = await supabase
    .from("user_charts")
    .select("name,chart")
    .eq("id", id);
  if (error) throw error;
  const chart = data?.[0];
  if (!chart) throw new Error("No chart found");

  const response = await makeChart({
    name: `Copy of ${chart.name}`,
    user_id: sessionData.session.user.id,
    chart: chart.chart,
  });

  if (!response) throw new Error("Unable to copy chart");
  if (response.error) throw response.error;

  return response.data[0];
}

const createFullTextSearchString = (searchString: string) => {
  return searchString
    .trim()
    .split(/[\s,\t,\n]+/) // split and remove more than 1 space
    .join(" | ");
};

async function userCharts({ queryKey }: any) {
  if (!supabase) return;
  const [userId, keywords] = queryKey;

  const query = supabase
    .from("user_charts")
    .select("id,name,created_at,updated_at,is_public,last_modified_by,user_id")
    .order("updated_at", { ascending: false })
    .eq("user_id", userId);

  if (keywords && keywords !== "") {
    query.textSearch("name", `${createFullTextSearchString(keywords)}`);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data.map((chart) => {
    const niceUpdatedDate = chart.updated_at
      ? `Updated ${formatDistanceStrict(
          parseISO(chart.updated_at),
          new Date()
        )} ago`
      : "";
    // nice created date like "March 10th, 2004"
    const niceCreatedDate = format(parseISO(chart.created_at), "MMMM do, yyyy");
    return {
      ...chart,
      niceUpdatedDate,
      niceCreatedDate,
    };
  });
}

/** We can manually pass the user id if we use this hook above the AppContext */
export function useHostedCharts(iUserId?: string, keywords?: string) {
  const { session } = useContext(AppContext);
  const userId = iUserId ?? session?.user?.id;
  const enabled = !!userId;
  return useQuery([userId, keywords, "auth", "hostedCharts"], userCharts, {
    suspense: false,
    enabled,
    refetchOnMount: true,
    staleTime: 0,
    retry: 1,
  });
}

async function userShareCharts({ queryKey }: any) {
  if (!supabase) return;
  const [email, keywords] = queryKey;

  const query = supabase
    .from("user_charts")
    .select("id,name,created_at,updated_at,is_public,last_modified_by,user_id")
    .order("updated_at", { ascending: false })
    .contains("shared_emails", [email]);

  if (keywords && keywords !== "") {
    query.textSearch("name", `${createFullTextSearchString(keywords)}`);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data.map((chart) => {
    const niceUpdatedDate = chart.updated_at
      ? `Updated ${formatDistanceStrict(
          parseISO(chart.updated_at),
          new Date()
        )} ago`
      : "";
    // nice created date like "March 10th, 2004"
    const niceCreatedDate = format(parseISO(chart.created_at), "MMMM do, yyyy");
    return {
      ...chart,
      niceUpdatedDate,
      niceCreatedDate,
    };
  });
}

export function useHostedShareCharts(iUserEmail?: string, keywords?: string) {
  const { session } = useContext(AppContext);
  const userEmail = iUserEmail ?? session?.user?.email;
  const enabled = !!userEmail;

  return useQuery(
    [userEmail, keywords, "auth", "hostedShareCharts"],
    userShareCharts,
    {
      suspense: false,
      enabled,
      refetchOnMount: true,
      staleTime: 0,
      retry: 1,
    }
  );
}

export async function getHostedChart(id?: string) {
  if (!id) return;
  if (!supabase) return;
  const { data, error } = await supabase
    .from("user_charts")
    .select(
      "id,name,chart,updated_at,created_at,public_id,is_public,shared_emails,user_id"
    )
    .eq("id", id);
  if (error) throw error;
  if (!data || data.length === 0) throw new Error("Invalid Chart ID");
  if (data.length > 1) throw new Error("Multiple Charts Found");
  return data[0];
}

export async function updateChartText(
  chart: string,
  modified_email: string,
  id?: string
) {
  if (!id) return;
  if (!supabase) return;
  return supabase
    .from("user_charts")
    .update({ chart, last_modified_by: modified_email, updated_at: new Date() })
    .eq("id", id);
}

export async function deleteChart({ chartId }: { chartId: number }) {
  if (!supabase) return;
  const { data, error } = await supabase
    .from("user_charts")
    .delete()
    .match({ id: chartId })
    .select();
  if (error) throw error;
  return data;
}

export function createSubscription({
  customerId,
  paymentMethodId,
  subscriptionType,
}: {
  customerId: string;
  paymentMethodId: string;
  subscriptionType: "monthly" | "yearly";
}) {
  return (
    fetch("/api/create-subscription", {
      method: "post",
      headers: {
        "Content-type": "application/json",
      },
      body: JSON.stringify({
        customerId,
        paymentMethodId,
        subscriptionType,
      }),
    })
      .then((response) => {
        return response.json();
      })
      // If the card is declined, display an error to the user.
      .then((result) => {
        if (result.error) {
          // The card had an error when trying to attach it to a customer.
          throw result;
        }
        return result;
      })
      .then((result) => {
        return {
          paymentMethodId: paymentMethodId,
          subscription: result,
        };
      })
      .catch((error) => {
        return error;
      })
  );
}

/**
 * Rename a hosted chart and clear the related cache
 */
export async function renameChart(id: number, name: string) {
  if (!supabase) return;
  const { data, error } = await supabase
    .from("user_charts")
    .update({ name, updated_at: new Date() })
    .eq("id", id);
  if (error) throw error;
  // clear cache
  await queryClient.invalidateQueries(["useHostedDoc", id.toString()]);
  return data;
}

export async function makeChartPublic(id: number, isPublic: boolean) {
  if (!supabase) return;
  const { data, error } = await supabase
    .from("user_charts")
    .select("is_public,public_id")
    .eq("id", id);

  if (error) throw error;
  if (!data || data.length === 0) throw new Error("Invalid Chart");

  const { is_public, public_id } = data[0];
  if (is_public === isPublic) return;

  const r = {
    isPublic,
    publicId: public_id,
  };

  const result = await supabase
    .from("user_charts")
    .update({ is_public: isPublic, updated_at: new Date() })
    .eq("id", id);
  if (result.error) throw result.error;

  return r;
}

export async function storeChartSharedEmails(
  id: number,
  sharedEmails: string[]
) {
  if (!supabase) return;

  const result = await supabase
    .from("user_charts")
    .update({ shared_emails: sharedEmails, updated_at: new Date() })
    .eq("id", id);

  if (result.error) throw result.error;

  return { sharedEmails };
}

export async function getPublicChart(publicId: string) {
  if (!publicId) return;
  if (!supabase) return;
  const { data, error } = await supabase
    .from("user_charts")
    .select("id,name,chart,updated_at,created_at,public_id,is_public,password")
    .eq("public_id", publicId);
  if (error) throw error;
  if (!data || data.length === 0) throw new Error("Invalid Chart ID");
  if (data.length > 1) throw new Error("Multiple Charts Found");

  const result = data[0];
  if (!result.is_public) throw new Error("Chart is not public");

  return result;
}

export async function renderDiagram(
  diagramScript: string,
  diagramId: string,
  outputFormat: string,
  diagramOptions: Record<string, any>
): Promise<string> {
  let options: Record<string, any> = {};
  if (diagramId === "d2") {
    if (!diagramOptions.sketch || diagramOptions.sketch === false) {
      options = {
        theme: diagramOptions.themeName ?? "default",
        layout: diagramOptions.layoutName ?? "dagre",
      };
    } else {
      options = {
        theme: diagramOptions.themeName ?? "default",
        layout: diagramOptions.layoutName ?? "dagre",
        sketch: diagramOptions.sketch ?? false,
      };
    }
  } else if (diagramId === "mermaid") {
    options = {
      theme: diagramOptions.themeName ?? "default",
    };
  } else if (diagramId === "plantuml") {
    options = {
      theme: diagramOptions.themeName ?? "",
      "no-metadata": diagramOptions.noMetadata ?? false,
    };
  }

  const result = await fetch("https://diagram-render.tdadevelop.com/", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      diagram_source: diagramScript,
      diagram_type: diagramId,
      output_format: outputFormat,
      diagram_options: options,
    }),
  });

  const response = await result.text();
  return response;
}

export function useRenderDiagram(
  diagramScript: string,
  diagramId: string,
  outputFormat: string = "svg",
  diagramOptions: Record<string, any>
) {
  return useQuery(
    ["kroki", "renderDiagram", diagramScript, diagramId, outputFormat],
    () => renderDiagram(diagramScript, diagramId, outputFormat, diagramOptions),
    {
      enabled: Boolean(diagramScript) && Boolean(diagramId),
      suspense: true,
    }
  );
}
